import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import i18n from 'i18next';
import { t } from 'i18next';
import { useSnackbar } from 'notistack';
import SettingsBlock from '../../util/SettingsBlock';

const lbl: React.CSSProperties = {
  display: 'block', marginBottom: 5, fontSize: 11, fontWeight: 800,
  letterSpacing: '.03em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)',
};
const hint: React.CSSProperties = { fontSize: 11, color: 'var(--text-color-calendar-week)', marginTop: 4, display: 'block' };
const pad: React.CSSProperties = { padding: 'var(--space-l2)', display: 'flex', flexDirection: 'column', gap: 'var(--space-l2)' };

const localeOf = () => {
  const l = (i18n.language || 'en').slice(0, 2);
  return l === 'ua' ? 'uk' : l;
};
const MONTHS = Array.from({ length: 12 }, (_, m) => ({
  value: m + 1,
  label: new Date(2000, m, 1).toLocaleString(localeOf(), { month: 'long' }),
}));

interface Band { years: string; extraDays: string; }

interface Form {
  hr_allowPartialDayAbsence: boolean;
  hr_leaveYearStart: string;        // 'MM-DD'
  hr_leaveUnitBasis: string;
  hr_entitlementFormula: string;
  hr_entitlementWeeks: string;
  hr_accrualMode: string;
  hr_accrualRate: string;
  hr_rounding_day: string;
  hr_rounding_hour: string;
  hr_carryoverMaxDays: string;
  hr_timeStepMinutes: string;
  hr_dayDisplayMethod: string;
  hr_year_basis: string;
}

const DEFAULT_FORM: Form = {
  hr_allowPartialDayAbsence: false,
  hr_leaveYearStart: '01-01',
  hr_leaveUnitBasis: 'working_days',
  hr_entitlementFormula: 'weeks',
  hr_entitlementWeeks: '',
  hr_accrualMode: 'upfront',
  hr_accrualRate: '',
  hr_rounding_day: '1:0.5',
  hr_rounding_hour: '1:0.5',
  hr_carryoverMaxDays: '',
  hr_timeStepMinutes: '15',
  hr_dayDisplayMethod: 'actual',
  hr_year_basis: 'calendar',
};

const HrGeneralSettings: React.FC = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [form, setForm] = useState<Form>(DEFAULT_FORM);
  const [bands, setBands] = useState<Band[]>([]);
  const [saving, setSaving] = useState(false);
  const [customThr, setCustomThr] = useState<{ day: boolean; hour: boolean }>({ day: false, hour: false });

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get('user');
      const c = data?.company ?? {};
      setForm({
        hr_allowPartialDayAbsence: !!c.hr_allowPartialDayAbsence,
        hr_leaveYearStart: /^\d{2}-\d{2}$/.test(c.hr_leaveYearStart) ? c.hr_leaveYearStart : (c.hr_leaveYearStart === 'apr-6' ? '04-06' : '01-01'),
        hr_leaveUnitBasis: c.hr_leaveUnitBasis ?? 'working_days',
        hr_entitlementFormula: c.hr_entitlementFormula ?? 'weeks',
        hr_entitlementWeeks: c.hr_entitlementWeeks != null ? String(c.hr_entitlementWeeks) : '',
        hr_accrualMode: c.hr_accrualMode ?? 'upfront',
        hr_accrualRate: c.hr_accrualRate != null ? String(c.hr_accrualRate) : '',
        hr_rounding_day: c.hr_rounding_day ?? ({ full_up: 'up', half_up: 'up_half', nearest_half: 'nearest_half', tenth: 'none', none: 'none' } as Record<string, string>)[c.hr_rounding] ?? 'up',
        hr_rounding_hour: c.hr_rounding_hour ?? 'up',
        hr_carryoverMaxDays: c.hr_carryoverMaxDays != null ? String(c.hr_carryoverMaxDays) : '',
        hr_timeStepMinutes: c.hr_timeStepMinutes != null ? String(c.hr_timeStepMinutes) : '15',
        hr_dayDisplayMethod: c.hr_dayDisplayMethod ?? 'actual',
        hr_year_basis: c.hr_year_basis ?? 'calendar',
      });
      const sb = Array.isArray(c.hr_serviceBands) ? c.hr_serviceBands : [];
      setBands(sb.map((b: any) => ({ years: String(b.years ?? ''), extraDays: String(b.extraDays ?? '') })));
    } catch {
      // глобальный interceptor
    }
  }, []);
  useEffect(() => { load(); }, [load]);

  const set = (patch: Partial<Form>) => setForm(f => ({ ...f, ...patch }));

  const [mm, dd] = form.hr_leaveYearStart.split('-');
  const setMonth = (m: number) => set({ hr_leaveYearStart: `${String(m).padStart(2, '0')}-${dd || '01'}` });
  const setDay = (d: number) => set({ hr_leaveYearStart: `${mm || '01'}-${String(d).padStart(2, '0')}` });

  const addBand = () => setBands(b => [...b, { years: '', extraDays: '' }]);
  const setBand = (i: number, patch: Partial<Band>) => setBands(b => b.map((x, idx) => idx === i ? { ...x, ...patch } : x));
  const removeBand = (i: number) => setBands(b => b.filter((_, idx) => idx !== i));

  const save = async () => {
    setSaving(true);
    try {
      const serviceBands = bands
        .map(b => ({ years: Number(b.years), extraDays: Number(b.extraDays) }))
        .filter(b => !Number.isNaN(b.years) && !Number.isNaN(b.extraDays) && b.years >= 0 && b.extraDays > 0)
        .sort((a, b) => a.years - b.years);
      await axios.put('company/hr-settings', {
        hr_allowPartialDayAbsence: form.hr_allowPartialDayAbsence,
        hr_leaveYearStart: form.hr_leaveYearStart,
        hr_leaveUnitBasis: form.hr_leaveUnitBasis,
        hr_entitlementFormula: form.hr_entitlementFormula,
        hr_entitlementWeeks: form.hr_entitlementWeeks === '' ? null : Number(form.hr_entitlementWeeks),
        hr_accrualMode: form.hr_accrualMode,
        hr_accrualRate: form.hr_accrualRate === '' ? null : Number(form.hr_accrualRate),
        hr_rounding_day: form.hr_rounding_day,
        hr_rounding_hour: form.hr_rounding_hour,
        hr_carryoverMaxDays: form.hr_carryoverMaxDays === '' ? null : Number(form.hr_carryoverMaxDays),
        hr_timeStepMinutes: form.hr_timeStepMinutes === '' ? 15 : Number(form.hr_timeStepMinutes),
        hr_serviceBands: serviceBands,
        hr_dayDisplayMethod: form.hr_dayDisplayMethod,
        hr_year_basis: form.hr_year_basis,
      });
      enqueueSnackbar(t('Saved') as string, { variant: 'success' });
    } catch {
      enqueueSnackbar(t('Error') as string, { variant: 'error' });
    } finally { setSaving(false); }
  };

  const daysInMonth = new Date(2001, Number(mm || 1), 0).getDate();

  // Округление: шаг + порог (step:threshold). Разбор/сборка значения и пресеты.
  const STEP_OPTS: [string, string, string][] = [
    ['none', 'HrNoRounding', 'No rounding'], ['1', 'HrStepWhole', 'Whole'],
    ['0.5', 'HrStepHalf', '0.5'], ['0.25', 'HrStepQuarter', '0.25'], ['0.1', 'HrStepTenth', '0.1'],
  ];
  const THR_PRESETS: [string, string, string][] = [
    ['0', 'HrRuleAlwaysUp', 'Always up (≥0)'], ['0.25', 'HrRuleUp25', 'Up from 0.25'],
    ['0.3', 'HrRuleUp30', 'Up from 0.3'], ['0.5', 'HrRuleStandard', 'Standard (≥0.5)'],
    ['1', 'HrRuleAlwaysDown', 'Always down (≥1)'],
  ];
  const LEGACY_R: Record<string, [string, string]> = {
    up: ['1', '0'], full_up: ['1', '0'], up_half: ['0.5', '0'], half_up: ['0.5', '0'],
    nearest_half: ['0.5', '0.5'], tenth: ['0.1', '0.5'],
  };
  const parseR = (v?: string) => {
    if (!v || v === 'none') return { step: 'none', thr: '0.5' };
    if (LEGACY_R[v]) return { step: LEGACY_R[v][0], thr: LEGACY_R[v][1] };
    const [s, th] = v.split(':');
    return { step: s || '1', thr: th ?? '0.5' };
  };
  const composeR = (step: string, thr: string) => step === 'none' ? 'none' : `${step}:${thr}`;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-l2)' }}>
      <SettingsBlock title={t('HrLeaveCalc', 'Leave calculation') as string}>
        <div style={pad}>
          <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
            <input type="checkbox" checked={form.hr_allowPartialDayAbsence}
              onChange={e => set({ hr_allowPartialDayAbsence: e.target.checked })}
              style={{ width: 16, height: 16, marginTop: 2 }} />
            <span>
              <b style={{ fontSize: 13 }}>{t('HrAllowPartialDay')}</b><br />
              <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{t('HrAllowPartialDayHint')}</span>
            </span>
          </label>

          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 'var(--space-l2)' }}>
            <div>
              <label style={lbl}>{t('HrLeaveYearStart')}</label>
              <div style={{ display: 'flex', gap: 8 }}>
                <select className="timesheet-edit-input" style={{ flex: 1 }} value={Number(mm)} onChange={e => setMonth(Number(e.target.value))}>
                  {MONTHS.map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                </select>
                <select className="timesheet-edit-input" style={{ width: 80 }} value={Number(dd)} onChange={e => setDay(Number(e.target.value))}>
                  {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(d => <option key={d} value={d}>{d}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label style={lbl}>{t('HrLeaveUnitBasis')}</label>
              <select className="timesheet-edit-input w-full" value={form.hr_leaveUnitBasis} onChange={e => set({ hr_leaveUnitBasis: e.target.value })}>
                <option value="working_days">{t('HrUnitBasis_working_days')}</option>
                <option value="calendar_days">{t('HrUnitBasis_calendar_days')}</option>
                <option value="hours">{t('HrUnitBasis_hours')}</option>
              </select>
            </div>

            <div>
              <label style={lbl}>{t('HrEntitlementFormula')}</label>
              <select className="timesheet-edit-input w-full" value={form.hr_entitlementFormula} onChange={e => set({ hr_entitlementFormula: e.target.value })}>
                <option value="weeks">{t('HrFormula_weeks')}</option>
                <option value="days">{t('HrFormula_days')}</option>
                <option value="hours">{t('HrFormula_hours')}</option>
              </select>
            </div>

            <div>
              <label style={lbl}>{t('HrEntitlementWeeks')}</label>
              <input type="number" step="0.1" className="timesheet-edit-input w-full" value={form.hr_entitlementWeeks} onChange={e => set({ hr_entitlementWeeks: e.target.value })} />
            </div>

            <div>
              <label style={lbl}>{t('HrAccrualMode')}</label>
              <select className="timesheet-edit-input w-full" value={form.hr_accrualMode} onChange={e => set({ hr_accrualMode: e.target.value })}>
                <option value="upfront">{t('HrAccrual_upfront')}</option>
                <option value="monthly">{t('HrAccrual_monthly')}</option>
              </select>
            </div>

            <div>
              <label style={lbl}>{t('HrAccrualRate')}</label>
              <input type="number" step="0.1" className="timesheet-edit-input w-full" value={form.hr_accrualRate} onChange={e => set({ hr_accrualRate: e.target.value })} placeholder={t('HrAccrual_monthly') as string} />
            </div>

            <div>
              <label style={lbl}>{t('HrDayDisplayMethod', 'Day display method')}</label>
              <select className="timesheet-edit-input w-full" value={form.hr_dayDisplayMethod} onChange={e => set({ hr_dayDisplayMethod: e.target.value })}>
                <option value="actual">{t('HrDayDisplay_actual', 'Actual working days (÷ avg hrs/day)')}</option>
                <option value="proportional">{t('HrDayDisplay_proportional', 'Proportional to 5-day week (÷ hrs/week ÷ 5)')}</option>
              </select>
              <span style={hint}>{t('HrDayDisplayHint', 'How leave hours are converted to days for display. "Proportional" matches the accountant formula.')}</span>
            </div>

            <div>
              <label style={lbl}>{t('HrYearBasis', 'Pro-rata year basis')}</label>
              <select className="timesheet-edit-input w-full" value={form.hr_year_basis} onChange={e => set({ hr_year_basis: e.target.value })}>
                <option value="calendar">{t('HrYearBasis_calendar', 'Calendar days (365 / 366 in leap year)')}</option>
                <option value="working">{t('HrYearBasis_working', 'Working days (Mon–Fri, ~261 per year)')}</option>
              </select>
              <span style={hint}>{t('HrYearBasisHint', 'The day count used as denominator for pro-rata calculations when staff start mid-year. "Working days" matches payroll systems that use 261 as the annual base.')}</span>
            </div>

            <div>
              <label style={lbl}>{t('HrCarryoverMax')}</label>
              <input type="number" step="0.5" className="timesheet-edit-input w-full" value={form.hr_carryoverMaxDays} onChange={e => set({ hr_carryoverMaxDays: e.target.value })} />
            </div>

            <div>
              <label style={lbl}>{t('HrTimeStep', 'Time step') as string}</label>
              <select className="timesheet-edit-input w-full" value={form.hr_timeStepMinutes} onChange={e => set({ hr_timeStepMinutes: e.target.value })}>
                <option value="1">1 {t('Min')}</option>
                <option value="10">10 {t('Min')}</option>
                <option value="15">15 {t('Min')}</option>
                <option value="30">30 {t('Min')}</option>
              </select>
              <span style={hint}>{t('HrTimeStepHint', 'Applies to leave hours and timesheets')}</span>
            </div>
          </div>

          {/* ── Rounding — отдельный раздел, чтобы не путать со ставкой/начислением ── */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '4px 0 2px' }}>
            <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.06em', textTransform: 'uppercase', color: 'var(--text-color-calendar-week)', whiteSpace: 'nowrap' }}>{t('HrRounding', 'Rounding')}</span>
            <span style={{ flex: 1, height: 1, background: 'var(--border-input-one)' }} />
          </div>
          <span style={{ ...hint, marginTop: -2 }}>{t('HrRoundingIntro', 'How the yearly entitlement is rounded. Threshold = fraction of a step from which it rounds up (0 = always up, 0.5 = standard, 1 = always down).')}</span>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: 'var(--space-l2)' }}>
            {(['day', 'hour'] as const).map(k => {
              const field = k === 'day' ? 'hr_rounding_day' : 'hr_rounding_hour';
              const { step, thr } = parseR(form[field]);
              const isCustom = customThr[k] || (step !== 'none' && !THR_PRESETS.some(([v]) => v === thr));
              return (
                <div key={k} style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={lbl}>{k === 'day' ? t('HrRoundingDay', 'Rounding (days)') : t('HrRoundingHour', 'Rounding (hours)')}</label>
                  <div style={{ display: 'flex', gap: 8 }}>
                    <select className="timesheet-edit-input" style={{ flex: 1 }} value={step}
                      onChange={e => { setCustomThr(c => ({ ...c, [k]: false })); set({ [field]: composeR(e.target.value, thr) } as any); }}>
                      {STEP_OPTS.map(([v, lblKey, def]) => <option key={v} value={v}>{t(lblKey, def)}</option>)}
                    </select>
                    {step !== 'none' && (
                      <select className="timesheet-edit-input" style={{ flex: 1.4 }} value={isCustom ? 'custom' : thr}
                        onChange={e => {
                          if (e.target.value === 'custom') { setCustomThr(c => ({ ...c, [k]: true })); }
                          else { setCustomThr(c => ({ ...c, [k]: false })); set({ [field]: composeR(step, e.target.value) } as any); }
                        }}>
                        {THR_PRESETS.map(([v, lblKey, def]) => <option key={v} value={v}>{t(lblKey, def)}</option>)}
                        <option value="custom">{t('HrRuleCustom', 'Custom…')}</option>
                      </select>
                    )}
                  </div>
                  {step !== 'none' && isCustom && (
                    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                      <span style={{ fontSize: 11, color: 'var(--text-color-calendar-week)' }}>{t('HrRoundUpFrom', 'Round up from')}</span>
                      <input type="number" min={0} max={1} step={0.05} className="timesheet-edit-input" style={{ width: 90 }}
                        title={t('HrRoundUpFrom', 'Round up from') as string}
                        value={thr} onChange={e => set({ [field]: composeR(step, e.target.value) } as any)} />
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          <span style={hint}>ℹ️ {t('HrHolidayPatternInfo')}</span>
        </div>
      </SettingsBlock>

      <SettingsBlock title={t('HrServiceBands', 'Extra days for length of service') as string}>
        <div style={pad}>
          {bands.length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 40px', gap: 8, ...lbl, marginBottom: 0 }}>
                <span>{t('HrServiceYears', 'Years of service ≥') as string}</span>
                <span>{t('HrExtraDays', 'Extra days') as string}</span>
                <span />
              </div>
              {bands.map((b, i) => (
                <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 40px', gap: 8, alignItems: 'center' }}>
                  <input type="number" min="0" className="timesheet-edit-input" value={b.years} onChange={e => setBand(i, { years: e.target.value })} />
                  <input type="number" min="0" step="0.5" className="timesheet-edit-input" value={b.extraDays} onChange={e => setBand(i, { extraDays: e.target.value })} />
                  <button type="button" className="btn-icon" title={t('Delete') as string} onClick={() => removeBand(i)}>🗑</button>
                </div>
              ))}
            </div>
          )}
          <button type="button" className="dt-btn-ghost" style={{ alignSelf: 'flex-start' }} onClick={addBand}>+ {t('HrAddBand', 'Add band') as string}</button>
          <span style={hint}>{t('HrServiceBandsHint', 'Auto-added to the main leave type based on hire date') as string}</span>
        </div>
      </SettingsBlock>

      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <button type="button" className="btn-primary" onClick={save} disabled={saving}>{t('Save')}</button>
      </div>
    </div>
  );
};

export default HrGeneralSettings;
